import { createAsyncThunk } from "@reduxjs/toolkit"
import { Navigate } from "react-router-dom"
import apiCall from "../../backend/http"

const kaleUser = createAsyncThunk(
  "auth/createKaleUser",
  async (postData, { rejectWithValue }) => {
    try {
      const enrichedPostData = {
        ...postData,
        created_at: new Date().toISOString(),
        user_type: "KALE",
        confirm_password: undefined
      }

      const { data } = await apiCall.post("/api/kale-users/", enrichedPostData)

      return {
        user: data,
        message: "User created successfully"
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message)
      return rejectWithValue(
        error.response ? error.response.data : { message: "Network error" }
      )
    }
  }
)

export const fetchAllKaleUsers = createAsyncThunk(
  "/auth/allKaleUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/kale-users/")
      console.log("API Response:", response.data)

      if (!response.data) {
        throw new Error("No data received")
      }

      return {
        users: response.data,
        total_users: response.data.length,
        users_by_department: response.data.reduce((acc, user) => {
          if (user.department) {
            acc[user.department] = (acc[user.department] || 0) + 1
          }
          return acc
        }, {})
      }
    } catch (error) {
      console.error("Error fetching Kale users:", error)

      if (error.response?.status === 401) {
        Navigate("/signIn")
        return rejectWithValue({
          message: "Session expired. Please login again."
        })
      }

      return rejectWithValue({
        message: error.response?.data?.message || "Failed to Retrieve users"
      })
    }
  }
)

export const TotalKaleUsers = createAsyncThunk(
  "/auth/totalKaleUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/kale-users/")

      if (!response.data) {
        throw new Error("No data received")
      }

      const totalUsers = response.data.length

      return totalUsers
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({
          message: "Session expired. Please login again."
        })
      }

      return rejectWithValue({
        message: error.response?.data?.message || "Failed to Retrieve users"
      })
    }
  }
)

export const createRole = createAsyncThunk(
  "roles/createRole",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await apiCall.post("/api/roles/", postData, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      })

      return {
        userRoles: response.data,
        message: "Role created successfully"
      }
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Failed to create role",
        status: error.response?.status,
        details: error.response?.data
      })
    }
  }
)

export const fetchRoles = createAsyncThunk(
  "roles/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/roles/")
      return {
        userRoles: response.data || [],
        message: "Roles fetched successfully"
      }
    } catch (error) {
      console.error("Fetch roles failed:", error)
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to fetch roles"
      })
    }
  }
)

export const TotalCustomers = createAsyncThunk(
  "/auth/totalCustomers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/statistics/users/")

      if (!response.data) {
        throw new Error("No data received")
      }
      return response.data.data || response.data
    } catch (error) {
      console.error("Error total Kale users:", error)

      if (error.response?.status === 401) {
        return rejectWithValue({
          message: "Session expired. Please login again."
        })
      }

      return rejectWithValue({
        message: error.response?.data?.message || "Failed to Retrieve users"
      })
    }
  }
)

export const createSupplier = createAsyncThunk(
  "auth/createSupplier",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await apiCall.post(
        "/api/suppliers/suppliers/",
        postData,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      )

      console.log("Supplier creation successful:", response.data)

      console.log("Full Response:", {
        data: response.data,
        headers: response.headers,
        status: response.status
      })
      return {
        data: response.data,
        message: "Supplier created successfully"
      }
    } catch (error) {
      console.error("Supplier creation failed:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      })

      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to create supplier"
      })
    }
  }
)

export const fetchSuppliers = createAsyncThunk(
  "supplier/fetchSuppliers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/suppliers/suppliers/")
      return {
        data: response.data,
        message: "Suppliers fetched successfully"
      }
    } catch (error) {
      console.error("Fetch suppliers failed:", error)
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to fetch suppliers"
      })
    }
  }
)

export const fetchSupplierById = createAsyncThunk(
  "supplier/fetchSupplierById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiCall.get(`/api/suppliers/${id}/`)
      return {
        data: response.data,
        message: "Supplier fetched successfully"
      }
    } catch (error) {
      console.error("Fetch supplier failed:", error.response)
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to fetch supplier"
      })
    }
  }
)

export const fetchIdTypes = createAsyncThunk(
  "supplier/fetchIdTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/suppliers/id_types/")
      return {
        data: response.data,
        message: "ID types fetched successfully"
      }
    } catch (error) {
      console.error("Fetch ID types failed:", error.response)
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to fetch ID types"
      })
    }
  }
)

export const updateSupplier = createAsyncThunk(
  "supplier/updateSupplier",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiCall.put(`/api/suppliers/${id}/`, data, {
        headers: {
          "Content-Type": "application/json"
        }
      })
      return {
        data: response.data,
        message: "Supplier updated successfully"
      }
    } catch (error) {
      console.error("Update supplier failed:", error.response)
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to update supplier"
      })
    }
  }
)

export const partialUpdateSupplier = createAsyncThunk(
  "supplier/partialUpdateSupplier",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiCall.patch(`/api/suppliers/${id}/`, data, {
        headers: {
          "Content-Type": "application/json"
        }
      })
      return {
        data: response.data,
        message: "Supplier partially updated successfully"
      }
    } catch (error) {
      console.error("Partial update supplier failed:", error.response)
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to update supplier"
      })
    }
  }
)

export const deleteSupplier = createAsyncThunk(
  "supplier/deleteSupplier",
  async (id, { rejectWithValue }) => {
    try {
      await apiCall.delete(`/api/suppliers/${id}/`)
      return {
        id,
        message: "Supplier deleted successfully"
      }
    } catch (error) {
      console.error("Delete supplier failed:", error.response)
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to delete supplier"
      })
    }
  }
)

export const createDriver = createAsyncThunk(
  "auth/createDriver",
  async (postData, { rejectWithValue }) => {
    try {
      const response = await apiCall.post("/api/drivers/drivers/", postData, {
        headers: {
          "Content-Type": "application/json"
        }
      })
      return {
        data: response.data,
        message: "Driver created successfully"
      }
    } catch (error) {
      console.error("Supplier creation failed:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      })

      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to create supplier"
      })
    }
  }
)

export const fetchDrivers = createAsyncThunk(
  "driver/fetchDrivers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall.get("/api/drivers/drivers/")

      return {
        data: Array.isArray(response.data)
          ? response.data
          : response.data.data || [],
        message: "Drivers fetched successfully"
      }
    } catch (error) {
      console.error("Fetch drivers failed:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      })
      return rejectWithValue({
        message: error.response?.data?.detail || "Failed to fetch drivers"
      })
    }
  }
)

export { kaleUser }
